import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Check, MapPin, X } from 'lucide-react';
import api from '../../utils/authUtils';
import { mediaUrl } from '../../utils/mediaUrl';

const BLUE = '#47ABDB';

// The actual decide-on-an-invitation content, pulled out of what used to be
// the whole of SpeakerInviteRespond.js so the exact same tested logic can
// render two ways: full-page there (still reachable from an old email link
// or the Speaker Hub) and inline, compact, right inside a profile's own
// engagement card (AttendeeProfile.js) - which is where a speaker actually
// lands after opening their invite now. Neither place answers the
// invitation by itself; only the two buttons here do, for every role/
// session of this event together.
const CardTitle = styled.h2`margin:0 0 .4rem;font-size:${p => (p.$compact ? '1.05rem' : '1.25rem')};color:#111D4E;`;
const Meta = styled.p`display:flex;flex-wrap:wrap;gap:.35rem 1rem;margin:0 0 1.1rem;color:#475569;font-size:.88rem;& span{display:inline-flex;align-items:center;gap:.3rem;}`;
const Cover = styled.img`display:block;width:100%;max-height:14rem;object-fit:cover;margin:0 0 1.1rem;`;
const Assignment = styled.div`border-left:3px solid ${BLUE};background:#f8fafc;padding:.65rem .85rem;margin-bottom:.55rem;font-size:.9rem;color:#1f2937;line-height:1.5;`;
const Label = styled.span`display:block;font-size:.7rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#64748b;`;
const Actions = styled.div`display:flex;gap:.65rem;flex-wrap:wrap;margin-top:1.1rem;`;
const Primary = styled.button`flex:1 1 10rem;display:inline-flex;justify-content:center;align-items:center;gap:.5rem;border:0;padding:.7rem 1rem;background:${BLUE};color:#fff;font-weight:700;font-size:.92rem;cursor:pointer;transition:background-color .2s;&:hover:not(:disabled){background:#0056b3;}&:disabled{opacity:.55;cursor:not-allowed;}`;
const Secondary = styled(Primary)`background:#fff;color:#334155;border:1px solid #cbd5e1;&:hover:not(:disabled){background:#f1f5f9;}`;
const DeclinePanel = styled.div`margin-top:1.2rem;border-top:1px solid #e2e8f0;padding-top:1.1rem;`;
const Field = styled.label`display:flex;flex-direction:column;gap:.3rem;font-size:.84rem;font-weight:600;color:#334155;margin-bottom:.75rem;`;
const Input = styled.input`padding:.55rem .65rem;border:1px solid #cbd5e1;font:inherit;font-weight:400;&:focus{outline:none;border-color:${BLUE};box-shadow:0 0 0 3px rgba(71,171,219,.15);}`;
const Hint = styled.p`margin:0 0 .9rem;color:#475569;font-size:.86rem;line-height:1.5;`;
const ErrorBox = styled.div`background:#fee2e2;color:#991b1b;padding:.7rem;margin-top:.9rem;font-size:.88rem;`;
const Notice = styled.div`color:#334155;line-height:1.55;font-size:.92rem;`;
const HubLink = styled(Link)`display:inline-block;margin-top:.75rem;color:#0369a1;font-weight:600;font-size:.88rem;`;
const Success = styled.div`background:#d1fae5;color:#065f46;padding:.7rem .85rem;margin-top:.9rem;font-size:.88rem;line-height:1.5;`;

const joinTimes = (start, end) => [start, end].filter(Boolean).join(' – ');

// `compact`: true inside AttendeeProfile.js's engagement card (no cover
// image, tighter spacing, no "go to the hub" link since you're already on
// your profile). `onResolved`/`onEventName` let an embedding page react -
// AttendeeProfile.js refreshes its history list; SpeakerInviteRespond.js
// shows the event name in its own headline.
const InviteDecisionPanel = ({ eventId, compact, onResolved, onEventName }) => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const [invitation, setInvitation] = useState(null);
  const [state, setState] = useState('loading'); // loading | open | answered | missing | declined | accepted
  const [declining, setDeclining] = useState(false);
  const [replacementName, setReplacementName] = useState('');
  const [replacementEmail, setReplacementEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/api/speaker/invitations/${eventId}`)
      .then(({ data }) => {
        setInvitation(data);
        setState('open');
        onEventName?.(data.eventName, data.eventNameEN);
      })
      .catch(err => setState(err.response?.status === 409 ? 'answered' : 'missing'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const roleLabel = role => t(`speaker.engagementRole.${role}`, role ? role.charAt(0) + role.slice(1).toLowerCase() : role);
  const failure = err => typeof err.response?.data === 'string' && err.response.data
    ? err.response.data : t('speaker.respond.err.generic', 'Something went wrong. Please try again.');

  const accept = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/api/speaker/invitations/${eventId}/accept`);
      setState('accepted');
      onResolved?.({ action: 'accepted', eventName: data.eventName, eventNameEN: data.eventNameEN });
    } catch (err) {
      if (err.response?.status === 409) setState('answered'); else setError(failure(err));
    } finally { setBusy(false); }
  };

  const decline = async e => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post(`/api/speaker/invitations/${eventId}/decline`, {
        replacementName: replacementName.trim() || null, replacementEmail: replacementEmail.trim() || null,
      });
      setState('declined');
      onResolved?.({ action: 'declined', eventName: invitation?.eventName, eventNameEN: invitation?.eventNameEN });
    } catch (err) {
      if (err.response?.status === 409) setState('answered'); else setError(failure(err));
    } finally { setBusy(false); }
  };

  if (state === 'loading') return <Notice>{t('speaker.respond.loading', 'Loading your invitation…')}</Notice>;
  if (state === 'answered' || state === 'missing') {
    return (
      <Notice>
        {state === 'answered'
          ? t('speaker.respond.answered', 'You have already answered this invitation – thank you.')
          : t('speaker.respond.missing', 'This invitation could not be found. It may have been withdrawn, or the event is already over.')}
        {!compact && <div><HubLink to="/speaker/dashboard">{t('speaker.respond.toHub', 'Go to your Speaker Hub')}</HubLink></div>}
      </Notice>
    );
  }
  if (state === 'accepted') {
    return (
      <Success>
        {t('speaker.respond.accepted.body', "You're confirmed - we look forward to welcoming you.")}
        {!compact && <div><HubLink to="/speaker/dashboard">{t('speaker.respond.toHub', 'Go to your Speaker Hub')}</HubLink></div>}
      </Success>
    );
  }
  if (state === 'declined') {
    const suggested = replacementName.trim() || replacementEmail.trim();
    return (
      <Notice>
        <CardTitle $compact={compact} as={compact ? 'h3' : 'h2'}>{t('speaker.respond.declined.title', 'Thank you for letting us know')}</CardTitle>
        {suggested
          ? t('speaker.respond.declined.withSuggestion', 'We have passed your answer and your suggestion on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')
          : t('speaker.respond.declined.body', 'We have passed your answer on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')}
        {!compact && <div><HubLink to="/speaker/dashboard">{t('speaker.respond.toHub', 'Go to your Speaker Hub')}</HubLink></div>}
      </Notice>
    );
  }

  const when = invitation.eventDateTime
    ? new Date(invitation.eventDateTime).toLocaleDateString(intl.locale, { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <>
      {!compact && invitation.eventImageUrl && <Cover src={mediaUrl(invitation.eventImageUrl)} alt="" onError={e => { e.currentTarget.style.display = 'none'; }} />}
      {!compact && (
        <>
          <CardTitle>{t('speaker.respond.title', 'You are invited to join us on stage')}</CardTitle>
          <Meta>
            {when && <span><Calendar size={15} />{when}</span>}
            {invitation.eventLocation && <span><MapPin size={15} />{invitation.eventLocation}</span>}
          </Meta>
        </>
      )}
      {(invitation.assignments || []).map((a, i) => (
        <Assignment key={i}>
          <Label>{roleLabel(a.role)}</Label>
          {a.sessionTitle && <div><strong>{a.sessionTitle}</strong>{joinTimes(a.sessionStartTime, a.sessionEndTime) && ` · ${joinTimes(a.sessionStartTime, a.sessionEndTime)}`}</div>}
          {a.talkTitle && <div>{t('speaker.respond.talk', 'Your talk: {title}', { title: a.talkTitle })}</div>}
          {joinTimes(a.talkStartTime, a.talkEndTime) && joinTimes(a.talkStartTime, a.talkEndTime) !== joinTimes(a.sessionStartTime, a.sessionEndTime) && (
            <div>{t('speaker.respond.talkTime', 'Time of your talk: {time}', { time: joinTimes(a.talkStartTime, a.talkEndTime) })}</div>
          )}
        </Assignment>
      ))}
      {(invitation.assignments || []).length > 1 && (
        <Hint style={{ marginTop: '.4rem' }}>{t('speaker.respond.allTogether', 'Your answer applies to everything listed above. If you would only like to take on part of it, please get in touch with the organizers before answering.')}</Hint>
      )}

      {!declining ? (
        <Actions>
          <Primary type="button" onClick={accept} disabled={busy}><Check size={18} />{t('speaker.respond.accept', 'Accept invitation')}</Primary>
          <Secondary type="button" onClick={() => setDeclining(true)} disabled={busy}><X size={18} />{t('speaker.respond.decline', 'Decline')}</Secondary>
        </Actions>
      ) : (
        <DeclinePanel as="form" onSubmit={decline}>
          <CardTitle as="h3" $compact style={{ fontSize: '1.02rem' }}>{t('speaker.respond.declineTitle', 'We are sorry you can\'t make it')}</CardTitle>
          <Hint>{t('speaker.respond.declineHint', 'Is there a colleague who could take your place? If you like, let us know who – both fields are optional.')}</Hint>
          <Field>
            {t('speaker.respond.replacementName', 'Name of your suggested replacement')}
            <Input value={replacementName} maxLength={200} autoComplete="off" onChange={e => setReplacementName(e.target.value)}
              placeholder={t('speaker.respond.replacementNamePlaceholder', 'e.g. Dr. Jane Doe')} />
          </Field>
          <Field>
            {t('speaker.respond.replacementEmail', 'Their email address')}
            <Input type="email" value={replacementEmail} maxLength={200} autoComplete="off" onChange={e => setReplacementEmail(e.target.value)}
              placeholder={t('speaker.respond.replacementEmailPlaceholder', 'name@example.com')} />
          </Field>
          <Actions style={{ marginTop: '.4rem' }}>
            <Primary type="submit" disabled={busy} style={{ background: '#b91c1c' }}>{t('speaker.respond.confirmDecline', 'Decline invitation')}</Primary>
            <Secondary type="button" onClick={() => { setDeclining(false); setError(''); }} disabled={busy}>{t('speaker.respond.back', 'Back')}</Secondary>
          </Actions>
        </DeclinePanel>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
    </>
  );
};

export default InviteDecisionPanel;
